import { useMemo } from 'react';
import {
  addDays, endOfMonth, monthGrid, startOfMonth,
  type CalendarEvent, type IsoDate, type Tag, type TaskInstance, type User,
} from '@hestia/core';
import { Avatar, Icon, icons, TagPills, hueVar, timeLabel } from '../ui.js';
import { EventRow } from '../components/EventRow.js';
import { dayNumber, fullDate, monthTitle } from '../lang.js';

interface Props {
  month: IsoDate;
  selected: IsoDate;
  today: IsoDate;
  instances: TaskInstance[];
  events: CalendarEvent[];
  calendarHues: Map<string, string>;
  users: Map<string, User>;
  tags: Map<string, Tag>;
  viewing: User | null;
  onMonth: (d: IsoDate) => void;
  onSelect: (d: IsoDate) => void;
  onOpen: (i: TaskInstance) => void;
  onToggle: (i: TaskInstance) => void;
}

const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export function Calendar({
  month, selected, today, viewing, instances, events, calendarHues, users, tags, onMonth, onSelect, onOpen, onToggle,
}: Props) {
  const grid = useMemo(() => monthGrid(month), [month]);

  // A task that runs over days belongs to every day it covers, the same way an
  // event does. The API sends it once, on the day it starts; the calendar is
  // where the length of it is the whole point, so it is laid across here.
  const byDay = useMemo(() => {
    const m = new Map<IsoDate, TaskInstance[]>();
    const add = (d: IsoDate, i: TaskInstance) => m.set(d, [...(m.get(d) ?? []), i]);
    for (const i of instances) {
      if (!i.endDate) { add(i.date, i); continue; }
      for (let d = i.date; d <= i.endDate; d = addDays(d, 1)) add(d, i);
    }
    return m;
  }, [instances]);

  /** Which days a span covers, and whether each is its first or last — enough
   *  to draw one continuous bar rather than a row of disconnected pills. */
  const spanBar = useMemo(() => {
    const m = new Map<IsoDate, { first: boolean; last: boolean }>();
    for (const i of instances) {
      if (!i.endDate || i.done) continue;
      for (let d = i.date; d <= i.endDate; d = addDays(d, 1)) {
        const at = m.get(d);
        m.set(d, { first: (at?.first ?? false) || d === i.date,
                   last: (at?.last ?? false) || d === i.endDate });
      }
    }
    return m;
  }, [instances]);

  // An event can span days, so a day shows it when the day falls inside the span.
  const eventsByDay = useMemo(() => {
    const m = new Map<IsoDate, CalendarEvent[]>();
    for (const e of events) {
      for (let d = e.startDate; d <= e.endDate; d = addDays(d, 1)) {
        m.set(d, [...(m.get(d) ?? []), e]);
        if (d > e.startDate && d >= e.endDate) break;
      }
    }
    return m;
  }, [events]);

  const monthPrefix = month.slice(0, 7);
  const agenda = byDay.get(selected) ?? [];
  const dayEvents = eventsByDay.get(selected) ?? [];

  return (
    <div className="page">
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 6 }}>
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          {viewing && <div className="sec">{viewing.displayName}</div>}
          <h1 className="dsp" style={{ fontSize: 27, margin: viewing ? '3px 0 0' : 0 }}>{monthTitle(month)}</h1>
        </div>
        <button className="pill" aria-label="Previous month"
                style={{ width: 42, height: 42, background: 'var(--glass)', color: 'var(--ink-2)' }}
                onClick={() => onMonth(addDays(startOfMonth(month), -1))}>
          <Icon path={icons.chevronLeft} size={18} />
        </button>
        <button className="pill" aria-label="Next month"
                style={{ width: 42, height: 42, background: 'var(--glass)', color: 'var(--ink-2)' }}
                onClick={() => onMonth(addDays(endOfMonth(month), 1))}>
          <Icon path={icons.chevronRight} size={18} />
        </button>
      </header>

      <div className="cal glass">
        <div className="cal-head">{WD.map((w, i) => <div className="cal-wd" key={i}>{w}</div>)}</div>
        <div className="cal-grid">
          {grid.map((d) => {
            const out = !d.startsWith(monthPrefix);
            const items = byDay.get(d) ?? [];
            const evs = eventsByDay.get(d) ?? [];
            // Up to three pips, coloured by list — the calendar's only colour.
            // Coloured by the task's first tag — the calendar's only colour.
            const pips = [
              ...items.map((i) => {
                const t = i.task.tagIds.length ? tags.get(i.task.tagIds[0]!) : undefined;
                return t ? hueVar(t.hue) : 'var(--ink-4)';
              }),
              ...evs.map((e) => hueVar(calendarHues.get(e.calendarId) ?? 'sky')),
            ].slice(0, 3);
            return (
              <button key={d} className={`cal-day${out ? ' out' : ''}${d === selected ? ' on' : ''}`}
                      aria-label={fullDate(d)} aria-current={d === today ? 'date' : undefined}
                      onClick={() => onSelect(d)}
                      style={d !== selected && d === today
                        ? { boxShadow: 'inset 0 0 0 1.5px var(--ink-4)' } : undefined}>
                {dayNumber(d)}
                {(() => {
                  const bar = spanBar.get(d);
                  return bar ? (
                    <span className={`cal-span${bar.first ? ' first' : ''}${bar.last ? ' last' : ''}`} />
                  ) : null;
                })()}
                <span className="pips">
                  {pips.map((c, n) => (
                    <span className="pip" key={n}
                          style={{ background: d === selected ? '#fff' : c }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <section className="section">
        <h2 className="dsp" style={{ fontSize: 20, margin: 0 }}>{fullDate(selected)}</h2>
        {agenda.length === 0 && dayEvents.length === 0 && (
          <div className="empty" style={{ justifyContent: 'center' }}>Nothing on this day.</div>
        )}
        {dayEvents.map((e) => (
          <EventRow key={e.id} event={e} hue={calendarHues.get(e.calendarId) ?? 'sky'} />
        ))}
        {agenda.map((i) => {
          const assignee = i.task.assigneeId ? users.get(i.task.assigneeId) : undefined;
          return (
            <div key={`${i.task.id}:${i.originalDate}`} className="row"
                 style={i.done ? { background: 'rgba(255,255,255,.5)', boxShadow: 'none' } : undefined}>
              <div className="agenda-time" style={i.done ? { color: 'var(--ink-3)' } : undefined}>
                {i.task.startTime ?? '—'}
              </div>
              <button style={{ flexGrow: 1, minWidth: 0, textAlign: 'left', padding: 0 }}
                      onClick={() => onOpen(i)}>
                <div className="row-title"
                     style={i.done ? { color: 'var(--ink-3)', textDecoration: 'line-through', fontWeight: 500 } : undefined}>
                  {i.task.title}
                </div>
                <div className="row-meta">
                  {timeLabel(i) && i.task.durationMin && (
                    <span className="chip" style={{ color: 'var(--ink-3)' }}>{timeLabel(i)}</span>
                  )}
                  <TagPills ids={i.task.tagIds} tags={tags} />
                </div>
              </button>
              <button className={`check${i.done ? ' on' : ''}`} aria-label="Toggle done"
                      onClick={() => onToggle(i)}>
                <Icon path={icons.check} size={12} w={3} />
              </button>
              <Avatar user={assignee} muted={i.done} size={28} />
            </div>
          );
        })}
      </section>
    </div>
  );
}
